import { DateTime } from 'luxon';
import { DateRange } from 'src/app/common/daterange';
import { sortByApiParam } from 'src/app/util/sorting';
import { GetStatsOptions } from '../stats/stats.service';
import { Screencast } from 'src/models';
import { buildDemoReportUsers, filterDemoReportUsers } from './demo-report-users.mock';
import { getDemoDateRangeFromOptions } from './demo-date-range.mock';

/** Local hours that receive screenshots on each workday (multiple per day). */
export const DEMO_SCREENSHOT_HOURS = [9, 10, 11, 14, 15] as const;

export interface DemoScreencastSeed {
  int(min: number, max: number, ...scope: (string | number)[]): number;
}

export interface DemoScreencastSlot {
  userId: string;
  at: DateTime;
  globalIndex: number;
}

function filterUsers(options: GetStatsOptions) {
  return filterDemoReportUsers(buildDemoReportUsers(), {
    user: options.user,
    tag: options.tag,
  });
}

function listWorkdaySlots(options: GetStatsOptions): DemoScreencastSlot[] {
  const dateRange = getDemoDateRangeFromOptions(options);
  const users = filterUsers(options);
  if (!users.length) {
    return [];
  }

  const slots: DemoScreencastSlot[] = [];
  let globalIndex = 0;
  let day = dateRange.end.startOf('day');
  const startDay = dateRange.start.startOf('day');

  while (day >= startDay) {
    if (day.weekday <= 5) {
      for (const hour of DEMO_SCREENSHOT_HOURS) {
        users.forEach((user, userIndex) => {
          slots.push({
            userId: user.id,
            at: day.set({
              hour,
              minute: userIndex * 3,
              second: 0,
              millisecond: 0,
            }),
            globalIndex: globalIndex++,
          });
        });
      }
    }
    day = day.minus({ days: 1 });
  }

  return slots;
}

function passesActivityFilter(avgActivity: number, options?: GetStatsOptions): boolean {
  const filter = options?.['filter[avg-activity]'];
  if (!filter) {
    return true;
  }
  const max = Number.parseInt(`${filter}`.replace('_', ''), 10);
  if (Number.isNaN(max)) {
    return true;
  }
  return avgActivity <= max;
}

export function countMockScreencasts(options?: GetStatsOptions): number {
  return listWorkdaySlots(options || {}).filter((slot) => {
    const avgActivity = 150 + (slot.globalIndex % 50);
    return passesActivityFilter(avgActivity, options);
  }).length;
}

export function buildMockScreencast(
  seed: DemoScreencastSeed,
  slot: DemoScreencastSlot,
): Screencast {
  const s = (field: string) => ['screencast', slot.globalIndex, slot.userId, field] as (string | number)[];
  const screenshotSlot = seed.int(0, 4, ...s('screenshot-slot'));
  const at = slot.at.toUTC();
  const avgActivity = 150 + seed.int(0, 49, ...s('avgActivity'));

  return {
    userId: slot.userId,
    date: at.toISO(),
    deviceId: `demo-screencast-device-${slot.globalIndex % 3}`,
    numbers: [
      {
        number: 0,
        mime: 'image/jpeg',
        deleted: false,
        avgActivity,
        meta: {
          chunkId: at.startOf('hour').toISO(),
          clicks: seed.int(0, 999, ...s('clicks')),
          movements: seed.int(0, 9999, ...s('movements')),
          keys: seed.int(0, 499, ...s('keys')),
          period: 2000 + seed.int(0, 199, ...s('period')),
          contentType: 'image/jpeg',
          imageSize: 180000 + seed.int(0, 19999, ...s('imageSize')),
          imageMd5: `md5-hash-${slot.globalIndex}`,
          videoMd5: `video-md5-hash-${slot.globalIndex}`,
          createdAt: at.toISO(),
          h: 1600 + seed.int(0, 99, ...s('h')),
          w: 2500 + seed.int(0, 199, ...s('w')),
          projectId: `project-${slot.globalIndex % 5}`,
          taskId: `task-${slot.globalIndex % 10}`,
          screenNumber: 0,
        },
        url: `assets/images/mock-data/screenshots/screenshot${screenshotSlot}.jpg`,
        urls: {
          original: `assets/images/mock-data/screenshots/screenshot${screenshotSlot}.jpg`,
          small: `assets/images/mock-data/screenshots/screenshot${screenshotSlot}.jpg`,
        },
        entity: 'screenshot',
      },
    ],
  };
}

export function buildMockScreencastsPage(
  seed: DemoScreencastSeed,
  options: GetStatsOptions | undefined,
  page: number,
  limit: number,
): Screencast[] {
  const slots = listWorkdaySlots(options || {}).filter((slot) => {
    const avgActivity = 150 + (slot.globalIndex % 50);
    return passesActivityFilter(avgActivity, options);
  });

  const sorted = sortByApiParam(
    slots.map(slot => buildMockScreencast(seed, slot)),
    options?.sort || '_date',
  );
  const start = page * limit;
  return sorted.slice(start, start + limit);
}

export function getMockScreencastDateRange(options?: GetStatsOptions): DateRange {
  return getDemoDateRangeFromOptions(options || {});
}
